import { account } from '../core/account.js';
import { i18n, t } from '../core/i18n.js';
import { notify } from '../core/notify.js';
import { profiles } from '../core/profiles.js';
import { escapeHtml, formatDate } from '../core/utils.js';

/* Solicitudes de acceso a tus proveedores (web con cuenta): una app de Android o Windows sin cuenta pide proveedores por tu correo y
   enseña un código de comprobación; aquí ves la petición con el mismo código, eliges qué concedes y aceptas o rechazas. Lo concedido
   se puede retirar. El que recibe no ve el host, el usuario ni la contraseña (aunque viajan a su app, como con el código de invitación). */

const SHAREABLE = ['xtream', 'm3u', 'jellyfin', 'emby', 'plex'];
let seq = 0;

export async function pendingRequestCount() {
    try { return ((await account.request('GET', '/api/share-requests')).requests || []).filter((r) => r.status === 'pending').length; } catch (e) { return 0; }
}

export function openAccessRequests({ onChange = () => {} } = {}) {
    const app = window.app;
    const id = 'accreq-' + (++seq) + '-' + Date.now().toString(36);
    const popup = app.popup.create({
        content: `<div class="popup catpick-popup admin-popup" id="${id}">
            <div class="view"><div class="page">
                <div class="navbar"><div class="navbar-bg"></div><div class="navbar-inner">
                    <div class="title">${escapeHtml(t('access.title'))}</div>
                    <div class="right"><a href="#" class="link popup-close" aria-label="${escapeHtml(t('common.close'))}"><span class="mi">close</span></a></div>
                </div></div>
                <div class="page-content licadm-content"><div data-adm><div class="spinner"></div></div></div>
            </div></div>
        </div>`,
        on: {
            open(p) {
                const body = p.el.querySelector('[data-adm]');
                let list = [];
                const call = (method, path, data) => account.request(method, path, data ? { body: data } : {});
                const hours = (ms) => Math.max(1, Math.round((ms - Date.now()) / 3600000));
                const mine = () => profiles.list().filter((x) => !x.shared && SHAREABLE.indexOf(x.type || 'xtream') !== -1);

                const card = (r) => r.status === 'pending'
                    ? `<div class="admin-user licadm-card">
                        <div class="licadm-head"><b>${escapeHtml(r.label)}</b><span class="tag">${escapeHtml(t('access.code', { code: r.code }))}</span></div>
                        <div class="admin-user-sub">${escapeHtml(t('access.pendingSub', { h: hours(r.expiresAt) }))}</div>
                        <div class="payadm-links">
                            <button type="button" class="btn btn-primary btn-sm" data-accept="${escapeHtml(r.id)}"><span class="mi">check</span>${escapeHtml(t('access.accept'))}</button>
                            <button type="button" class="btn btn-sm" data-reject="${escapeHtml(r.id)}"><span class="mi">close</span>${escapeHtml(t('access.reject'))}</button>
                        </div></div>`
                    : `<div class="admin-user licadm-card">
                        <div class="licadm-head"><b>${escapeHtml(r.label)}</b><span class="tag payadm-paid">${escapeHtml(t('access.granted'))}</span></div>
                        <div class="admin-user-sub">${escapeHtml(formatDate(r.createdAt, i18n.lang))} · ${escapeHtml((r.items || []).join(', ') || '—')}</div>
                        <div class="payadm-links"><button type="button" class="btn btn-sm" data-revoke="${escapeHtml(r.id)}"><span class="mi">link_off</span>${escapeHtml(t('access.revoke'))}</button></div></div>`;

                const paint = () => {
                    body.innerHTML = `<p class="px small muted">${escapeHtml(t('access.help'))}</p>
                        <div class="admin-list">${list.length ? list.map(card).join('') : `<div class="empty"><span class="mi">inbox</span><p>${escapeHtml(t('access.empty'))}</p></div>`}</div>`;
                };
                const load = async () => {
                    try { list = (await call('GET', '/api/share-requests')).requests || []; paint(); onChange(list.filter((r) => r.status === 'pending').length); } catch (e) { body.innerHTML = `<div class="phone-status is-error">${escapeHtml(t('common.errorNetwork'))}</div>`; }
                };

                /** Pantalla para elegir qué proveedores se conceden. */
                const choose = (req) => {
                    const opts = mine();
                    body.innerHTML = `<p class="px small muted">${escapeHtml(t('access.chooseHelp', { label: req.label, code: req.code }))}</p>
                        <div class="admin-list">${opts.length ? opts.map((o) => `<label class="admin-user licadm-card"><input type="checkbox" data-pick="${escapeHtml(o.id)}"/> <b>${escapeHtml(o.name || o.host || o.type)}</b></label>`).join('') : `<div class="empty"><span class="mi">dns</span><p>${escapeHtml(t('access.noProviders'))}</p></div>`}</div>
                        <div class="licadm-more"><button type="button" class="btn btn-primary btn-block" data-grant="${escapeHtml(req.id)}"${opts.length ? '' : ' disabled'}><span class="mi">check</span>${escapeHtml(t('access.grant'))}</button>
                        <button type="button" class="btn btn-block" data-back>${escapeHtml(t('common.back'))}</button></div>`;
                };

                body.addEventListener('click', async (e) => {
                    const acc = e.target.closest('[data-accept]');
                    if (acc) { const r = list.find((x) => x.id === acc.getAttribute('data-accept')); return r && choose(r); }
                    if (e.target.closest('[data-back]')) return paint();
                    const grant = e.target.closest('[data-grant]');
                    if (grant) {
                        const ids = Array.from(body.querySelectorAll('[data-pick]:checked')).map((x) => x.getAttribute('data-pick'));
                        if (!ids.length) return notify.toast(t('access.pickOne'), { icon: 'info', duration: 2500 });
                        try { await call('POST', '/api/share-requests/' + encodeURIComponent(grant.getAttribute('data-grant')) + '/accept', { profileIds: ids }); notify.toast(t('access.granted'), { icon: 'check_circle', duration: 2500 }); await load(); } catch (err) { notify.error(t(err && err.code === 'not_pending' ? 'access.expired' : 'common.errorNetwork')); await load(); }
                        return;
                    }
                    const rej = e.target.closest('[data-reject]');
                    if (rej) { try { await call('POST', '/api/share-requests/' + encodeURIComponent(rej.getAttribute('data-reject')) + '/reject'); await load(); } catch (err) { notify.error(t('common.errorNetwork')); } return; }
                    const rev = e.target.closest('[data-revoke]');
                    if (rev) {
                        return notify.confirm(t('access.revokeConfirm'), t('access.revoke'), async () => {
                            try { await call('POST', '/api/share-requests/' + encodeURIComponent(rev.getAttribute('data-revoke')) + '/revoke'); notify.toast(t('access.revoked'), { icon: 'link_off', duration: 2500 }); await load(); } catch (err) { notify.error(t('common.errorNetwork')); }
                        });
                    }
                });
                load();
            },
            closed(p) { try { p.destroy(); } catch (e) { /* ya cerrado */ } }
        }
    });
    popup.open();
}
